/**
 * Opportunity aggregation for campaign report directories — PURE, app-import-free.
 *
 * When the admin enables opportunities for a campaign template, each directory
 * row (company or lead) carries a ONE-LINE digest of its strongest detected
 * opportunity: the top-confidence record across the enabled types, rendered as
 * "TYPE · 95% — summary" under the row.
 */
import type { CampaignRowOpportunity } from './schema/campaign';
import type { CampaignOpportunityTypeKey } from './campaign-config';

/** Raw n8n opportunity record (types.ts CampaignOpportunity JSON shape). */
type RawOpportunity = Record<string, any>;

/** Summary cap — one readable line in the directory row. */
const SUMMARY_MAX = 300;

const safeList = (v: unknown): RawOpportunity[] => {
  let parsed = v;
  if (typeof v === 'string') { try { parsed = JSON.parse(v); } catch { return []; } }
  return Array.isArray(parsed) ? parsed.filter((o): o is RawOpportunity => !!o && typeof o === 'object' && !Array.isArray(o)) : [];
};

/** USD range display ("$200,000–$350,000") — the Sellers card convention. */
const usd = (n: any) => typeof n === 'number' && Number.isFinite(n)
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n) : '';
export const formatUsd = (value: any): string => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '';
  const min = usd(value.min), max = usd(value.max);
  return min && max ? `${min}–${max}` : min || max;
};

/** Timeline label ("1–3 months") — mirrors companyInsights.formatTimeline. */
export const formatTimeline = (value: any): string => {
  const v = String(value ?? '').trim();
  if (!v) return '';
  const normalized = v.replace(/\s*-\s*/g, '–');
  return /month/i.test(normalized) ? normalized : `${normalized} months`;
};

/**
 * Digest one lead's opportunities (campaign-scoped + general, already gated by
 * the enabled types) into the strongest single line: highest numeric
 * confidence wins, ties and missing values defer to order. Prose: need, or
 * need + value/timeline when they fit the line budget. Returns undefined when
 * the lead has no usable record — the row simply renders without the line.
 */
export function opportunityDigest(
  lead: { campaignOpportunities?: unknown; generalOpportunities?: unknown },
  types: readonly CampaignOpportunityTypeKey[],
): CampaignRowOpportunity | undefined {
  if (!types.length) return undefined;
  let best: { conf: number; type: string; summary: string } | undefined;
  for (const type of types) {
    for (const o of safeList(type === 'campaign' ? lead.campaignOpportunities : lead.generalOpportunities)) {
      // Same usable-prose rule as everywhere else: no need/description → no line.
      const need = String(o.need ?? o.description ?? '').trim();
      if (!need) continue;
      const conf = Number(o.confidence);
      const rank = Number.isFinite(conf) ? conf : -1;
      if (best && rank <= best.conf) continue;
      // Compact prose: the need, plus value/timeline context when the line has room.
      const value = formatUsd(o.amount_usd);
      const timeline = formatTimeline(o.timeline_months);
      const context = [value, timeline].filter(Boolean).join(' · ');
      const summary = context && need.length + context.length + 3 <= SUMMARY_MAX
        ? `${need} (${context})`
        : need.slice(0, SUMMARY_MAX);
      best = { conf: rank, type: String(o.type ?? '').trim(), summary };
    }
  }
  if (!best) return undefined;
  const confNum = Number(best.conf);
  return {
    summary: best.summary,
    ...(best.type ? { type: best.type } : {}),
    ...(Number.isFinite(confNum) ? { confidence: String(best.conf) } : {}),
  };
}
